/**
 * useAccountRestrictions — the open restrictions on the caller's current
 * (default) billable account, derived from the data-driven `restrictions[]`
 * array the accounts read now returns (fraud/dispute epic §5.1b).
 *
 * Server state rides useBillableAccounts (TanStack Query), so restrictions stay
 * live off the same `credit-balance-changed` WebSocket signal that refreshes
 * balances — a freeze/clawback/restore updates the restriction set without a
 * bespoke channel.
 *
 * The single most-severe CRITICAL restriction (if any) gates the account via
 * RestrictedAccountGate; the rest render as banners. Ordering within a severity
 * is server-defined (we take the first critical).
 */
import { useMemo } from "react";
import { useBillableAccounts } from "./useBillableAccounts";
import type { AccountRestriction } from "../services/billingApi";

export function useAccountRestrictions() {
  const { activeAccount, isLoading, error } = useBillableAccounts();

  const restrictions = useMemo<AccountRestriction[]>(
    () => activeAccount?.restrictions ?? [],
    [activeAccount?.restrictions],
  );

  const criticalRestriction = useMemo(
    () => restrictions.find((r) => r.severity === "critical") ?? null,
    [restrictions],
  );

  return {
    restrictions,
    criticalRestriction,
    hasCritical: criticalRestriction !== null,
    isLoading,
    error,
  };
}
